import { useQuery, useMutation, useQueryClient, QueryClient, skipToken } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { jobEndApi } from '@/api/jobEndsApi';
import type { ActivityRating, FeedbackItemRequest, JobEndDetail, SkillRatingItem, SkillRatingScale, ActiveJobEndsFilterRequest } from '@/api/types/jobEnd.types';
import { DASHBOARD_QUERY_KEY } from './useDashboard';
import { PERMISSIONS_QUERY_KEY } from './usePermissions';
import { seniorFeedbackKeys } from './useSeniorFeedback';

/**
 * Invalidate job-end-page caches after a 409 conflict so the component
 * re-fetches a fresh RowVersion before retrying. Senior feedback is also
 * invalidated because it uses the same parent RowVersion; any 403 from
 * that optional endpoint is handled gracefully by the apiClient interceptor.
 */
export function invalidateJobEndPageOnConflict(queryClient: QueryClient, jobEndId: string | number) {
  const id = String(jobEndId);
  queryClient.invalidateQueries({ queryKey: jobEndKeys.detail(id) });
  queryClient.invalidateQueries({ queryKey: seniorFeedbackKeys.byJobEnd(Number(id)) });
}

export const jobEndKeys = {
  all: ['jobEnds'] as const,
  detail: (id: string) => ['jobEnd', id] as const,
  eligibleDelegates: (id: string) => ['eligibleDelegates', id] as const,
  eligibleAuthors: (id: string) => ['eligibleAuthors', id] as const,
  auditHistory: (id: string) => ['jobEnd', id, 'auditHistory'] as const,
  skillAreaDetails: (jobEndId: number, areaId: number | null) => ['jobEnd', jobEndId, 'skillArea', areaId] as const,
  activeJobEnds: (filter: ActiveJobEndsFilterRequest) => ['activeJobEnds', filter] as const,
};

export function useJobEndDetails(id: string) {
  return useQuery({
    queryKey: jobEndKeys.detail(id),
    queryFn: () => jobEndApi.getById(Number(id)),
    enabled: !!id,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        return false;
      }
      return failureCount < 1;
    },
  });
}

export function useEligibleDelegates(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: jobEndKeys.eligibleDelegates(id),
    queryFn: () => jobEndApi.getEligibleDelegates(Number(id)),
    enabled: !!id && enabled,
  });
}

export function useDelegateJobEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, delegateStaffId, rowVersion }: { id: number; delegateStaffId: number; rowVersion?: string }) =>
      jobEndApi.delegate(id, delegateStaffId, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
    },
  });
}

export function useApproveDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rowVersion }: { id: number; rowVersion?: string }) =>
      jobEndApi.approveDelegation(id, rowVersion),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: jobEndKeys.detail(String(id)) });
      queryClient.invalidateQueries({ queryKey: [...PERMISSIONS_QUERY_KEY, 'job-end', String(id)] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    },
  });
}

export function useRefuseDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rowVersion }: { id: number; rowVersion?: string }) =>
      jobEndApi.refuseDelegation(id, rowVersion),
    onSuccess: (_, { id }) => {
      // Remove cache instead of invalidating - user loses access after refusing,
      // so a refetch would cause a 403 and redirect to login
      queryClient.removeQueries({ queryKey: jobEndKeys.detail(String(id)) });
      queryClient.removeQueries({ queryKey: [...PERMISSIONS_QUERY_KEY, 'job-end', String(id)] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    },
    onError: (error, { id }) => {
      // Some environments return 401/403 after successful refuse due to immediate access revocation.
      // Clear stale JE page queries to stop refetch loops and allow UI to navigate away gracefully.
      if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        queryClient.removeQueries({ queryKey: jobEndKeys.detail(String(id)) });
        queryClient.removeQueries({ queryKey: [...PERMISSIONS_QUERY_KEY, 'job-end', String(id)] });
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      }
    },
  });
}

// Sections that own a child collection send the whole set on every click and guard on the parent RowVersion, so
// overlapping requests for one Job End would all carry the RowVersion the first of them started with. TanStack Query
// runs overlapping mutate() calls concurrently, so each request is chained behind the previous one and reads the
// RowVersion only once its turn comes. Keyed by Job End - different Job Ends never contend.
//
// The map hangs off the QueryClient rather than a per-hook useRef because every section on the Job End page guards on
// the same parent RowVersion: a queue per hook lets activity ratings and skill ratings collide with each other, which
// is the conflict this exists to prevent. Keying on the client keeps one queue per app while giving each test its own
// (createWrapper builds a fresh QueryClient), so a mutation left in flight by one test cannot stall the next.
const requestQueuesByClient = new WeakMap<QueryClient, Map<number, Promise<unknown>>>();

function useRequestQueuePerJobEnd() {
  const queryClient = useQueryClient();

  return async function enqueue<T>(id: number, send: () => Promise<T>): Promise<T> {
    let pending = requestQueuesByClient.get(queryClient);
    if (!pending) {
      pending = new Map();
      requestQueuesByClient.set(queryClient, pending);
    }

    const previous = pending.get(id) ?? Promise.resolve();
    const promise = previous.then(send, send);
    pending.set(id, promise);

    try {
      return await promise;
    } finally {
      if (pending.get(id) === promise) {
        pending.delete(id);
      }
    }
  };
}

export function useUpdateActivityRatings() {
  const queryClient = useQueryClient();
  const enqueue = useRequestQueuePerJobEnd();

  return useMutation({
    mutationFn: ({ id, ratings, rowVersion }: { id: number; ratings: ActivityRating[]; rowVersion?: string }) =>
      enqueue(id, async () => {
        const cached = queryClient.getQueryData<JobEndDetail>(jobEndKeys.detail(String(id)));
        const data = await jobEndApi.updateActivityRatings(id, ratings, cached?.rowVersion ?? rowVersion);
        queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
        return data;
      }),
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, id);
      }
    },
  });
}

export function useUpdateStrengthFeedbacks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, items, rowVersion }: { id: number; items: FeedbackItemRequest[]; rowVersion?: string }) =>
      jobEndApi.updateStrengthFeedbacks(id, items, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
    },
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, id);
      }
    },
  });
}

export function useUpdateDevelopmentAreaFeedbacks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, items, rowVersion }: { id: number; items: FeedbackItemRequest[]; rowVersion?: string }) =>
      jobEndApi.updateDevelopmentAreaFeedbacks(id, items, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
    },
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, id);
      }
    },
  });
}

export function useJobEndAuditHistory(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: jobEndKeys.auditHistory(id),
    queryFn: () => jobEndApi.getAuditHistory(Number(id)),
    enabled: !!id && enabled,
  });
}

export function useUpdateSkillRatings() {
  const queryClient = useQueryClient();
  const enqueue = useRequestQueuePerJobEnd();

  return useMutation({
    mutationFn: ({ id, ratings, rowVersion }: { id: number; ratings: SkillRatingItem[]; rowVersion?: string }) =>
      enqueue(id, async () => {
        const cached = queryClient.getQueryData<JobEndDetail>(jobEndKeys.detail(String(id)));
        const data = await jobEndApi.updateSkillRatings(id, ratings, cached?.rowVersion ?? rowVersion);
        queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
        return data;
      }),
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, id);
      }
    },
  });
}

export function useUpdateOverallSkillRating() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rating, rowVersion }: { id: number; rating: SkillRatingScale | null; rowVersion?: string }) =>
      jobEndApi.updateOverallSkillRating(id, rating, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
    },
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, id);
      }
    },
  });
}

export function useFinalizeJobEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rowVersion }: { id: number; rowVersion?: string }) =>
      jobEndApi.finalize(id, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
      queryClient.invalidateQueries({ queryKey: jobEndKeys.auditHistory(String(id)) });
      queryClient.invalidateQueries({ queryKey: [...PERMISSIONS_QUERY_KEY, 'job-end', String(id)] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    },
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, id);
      }
    },
  });
}

export function useAcknowledgeDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rowVersion }: { id: number; rowVersion?: string }) =>
      jobEndApi.acknowledgeDelivery(id, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
      queryClient.invalidateQueries({ queryKey: jobEndKeys.auditHistory(String(id)) });
      queryClient.invalidateQueries({ queryKey: [...PERMISSIONS_QUERY_KEY, 'job-end', String(id)] });
    },
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        invalidateJobEndPageOnConflict(queryClient, id);
      }
    },
  });
}

export function useSkillAreaDetails(jobEndId: number, areaId: number | null, enabled: boolean = true) {
  return useQuery({
    queryKey: jobEndKeys.skillAreaDetails(jobEndId, areaId),
    queryFn: areaId == null ? skipToken : () => jobEndApi.getSkillAreaDetails(jobEndId, areaId),
    enabled: enabled && !!jobEndId && !!areaId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Admin hooks

export function useActiveJobEnds(filter: ActiveJobEndsFilterRequest) {
  return useQuery({
    queryKey: jobEndKeys.activeJobEnds(filter),
    queryFn: () => jobEndApi.getActiveJobEnds(filter),
    placeholderData: (previousData) => previousData,
  });
}

export function useEligibleAuthors(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: jobEndKeys.eligibleAuthors(id),
    queryFn: () => jobEndApi.getEligibleAuthors(Number(id)),
    enabled: !!id && enabled,
  });
}

export function useEditJobEndAuthor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newCollatorStaffId, rowVersion }: { id: number; newCollatorStaffId: number; rowVersion?: string }) =>
      jobEndApi.editAuthor(id, newCollatorStaffId, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
      queryClient.invalidateQueries({ queryKey: jobEndKeys.auditHistory(String(id)) });
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}

export function useSendJobEndReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => jobEndApi.sendReminder(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: jobEndKeys.auditHistory(String(id)) });
    },
  });
}

export function useReissueJobEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rowVersion }: { id: number; rowVersion?: string }) =>
      jobEndApi.reissue(id, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
      queryClient.invalidateQueries({ queryKey: jobEndKeys.auditHistory(String(id)) });
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}

export function useReopenJobEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rowVersion }: { id: number; rowVersion?: string }) =>
      jobEndApi.reopen(id, rowVersion),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(jobEndKeys.detail(String(id)), data);
      queryClient.invalidateQueries({ queryKey: jobEndKeys.auditHistory(String(id)) });
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}

export function useDeleteJobEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => jobEndApi.deleteJobEnd(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}

// Bulk action hooks

export function useBulkEditJobEndAuthor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobEndIds, newCollatorStaffId }: { jobEndIds: number[]; newCollatorStaffId: number }) =>
      jobEndApi.bulkEditAuthor(jobEndIds, newCollatorStaffId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}

export function useBulkSendReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobEndIds: number[]) => jobEndApi.bulkSendReminder(jobEndIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}

export function useBulkReissueJobEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobEndIds: number[]) => jobEndApi.bulkReissue(jobEndIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}

export function useBulkDeleteJobEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobEndIds: number[]) => jobEndApi.bulkDelete(jobEndIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeJobEnds'] });
    },
  });
}
